import moment from 'moment';
import { AxiosPrivado } from '../componentes/axios/Axios';
import {
  DashboardEmpleadoResumen,
  VentaFiltro,
  PedidosFiltro,
  CotizacionesFiltro,
} from '../configuracion/apiUrls';

/**
 * Identifica de forma robusta si una venta es a crédito bajo cualquier nomenclatura
 * (soporta 'Crédito' con acento, 'Credito' sin acento, relaciones de creditos, saldo pendiente, etc.)
 */
export const esVentaCredito = (v) => {
  if (!v) return false;
  if (v.tieneCredito === true) return true;
  if (v.tieneCredito === false) return false;
  if (v.creditoId != null && Number(v.creditoId) > 0) return true;
  if (Array.isArray(v.creditos) && v.creditos.length > 0) return true;
  if (v.credito && typeof v.credito === 'object' && Object.keys(v.credito).length > 0) return true;
  const tipo = String(v.tipoVenta || v.tipo || '').trim().toLowerCase();
  if (tipo.includes('cred') || tipo === 'cr') return true;
  if (Number(v.saldoCredito || 0) > 0) return true;
  return false;
};

export const DashboardServicio = {
  /**
   * Obtiene el resumen del dashboard operativo para un empleado o consolidado de toda la empresa
   * @param {number|string} empleadoId ID del empleado o 'TODOS' para vista consolidada
   * @param {string|null} fecha Fecha de corte YYYY-MM-DD
   */
  async obtenerResumenEmpleado(empleadoId, fecha = null) {
    const fechaQuery = fecha || moment().format('YYYY-MM-DD');
    const esConsolidadoGlobal = empleadoId === 'TODOS' || !empleadoId;

    // Ventanas de tiempo para sincronización en vivo
    const hoyInicio = moment().startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const hoyFin = moment().endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const mesInicio = moment().startOf('month').format('YYYY-MM-DD HH:mm:ss');
    const ultimos60Dias = moment().subtract(60, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const proximos30Dias = moment().add(30, 'days').endOf('day').format('YYYY-MM-DD HH:mm:ss');

    // Consultas concurrentes: Endpoint de Carlos + Endpoints vivos de base de datos
    const peticiones = [
      // 0: Endpoint oficial de Carlos (si no es vista consolidada general)
      !esConsolidadoGlobal
        ? AxiosPrivado.get(
            `${DashboardEmpleadoResumen}?idEmpleado=${empleadoId}&empleadoId=${empleadoId}&fecha=${fechaQuery}`,
            { timeout: 7000 }
          )
        : Promise.reject(new Error('Consolidado')),

      // 1: Ventas de Hoy en vivo
      AxiosPrivado.get(
        `${VentaFiltro}?fechaInicio=${encodeURIComponent(hoyInicio)}&fechaFin=${encodeURIComponent(hoyFin)}`,
        { timeout: 7000 }
      ),

      // 2: Ventas del Mes en vivo
      AxiosPrivado.get(
        `${VentaFiltro}?fechaInicio=${encodeURIComponent(mesInicio)}&fechaFin=${encodeURIComponent(hoyFin)}`,
        { timeout: 7000 }
      ),

      // 3: Pedidos en curso
      AxiosPrivado.get(
        `${PedidosFiltro}?fechaInicio=${encodeURIComponent(ultimos60Dias)}&fechaFin=${encodeURIComponent(proximos30Dias)}`,
        { timeout: 7000 }
      ),

      // 4: Cotizaciones del mes
      AxiosPrivado.get(
        `${CotizacionesFiltro}?fechaInicio=${encodeURIComponent(mesInicio)}&fechaFin=${encodeURIComponent(hoyFin)}`,
        { timeout: 7000 }
      ),
    ];

    const [resCarlos, resVentasHoy, resVentasMes, resPedidos, resCotizaciones] =
      await Promise.allSettled(peticiones);

    const parseArray = (res) => {
      if (res.status !== 'fulfilled' || !res.value?.data) return [];
      const d = res.value.data;
      if (Array.isArray(d)) return d;
      if (Array.isArray(d.data)) return d.data;
      if (Array.isArray(d.ventas)) return d.ventas;
      if (Array.isArray(d.pedidos)) return d.pedidos;
      if (Array.isArray(d.cotizaciones)) return d.cotizaciones;
      return [];
    };

    const rawCarlos = resCarlos.status === 'fulfilled' ? resCarlos.value?.data : null;

    // Filtro por colaborador en memoria: admite coincidencia directa o registros del usuario activo
    const perteneceAEmpleado = (item, targetEmpId) => {
      if (!targetEmpId || targetEmpId === 'TODOS') return true;
      const target = Number(targetEmpId);
      const empIdItem = Number(
        item.empleadoId ||
        item.Empleado?.id ||
        item.EmpleadoId ||
        item.empleado?.id ||
        item.idEmpleado ||
        item.usuarioId ||
        0
      );
      if (empIdItem === target) return true;
      // Si la transacción no tiene empleado asignado (0 o null), no excluirla para que el usuario la vea reflejada
      if (empIdItem === 0) return true;
      return false;
    };

    // ----------------------------------------------------
    // A. VENTAS DE HOY (Separación exacta Contado / Crédito)
    // ----------------------------------------------------
    const ventasHoyReales = parseArray(resVentasHoy).filter((v) =>
      perteneceAEmpleado(v, empleadoId)
    );

    // Integrar ventasHoyDetalle del backend si Carlos las incluye
    if (Array.isArray(rawCarlos?.ventasHoyDetalle)) {
      rawCarlos.ventasHoyDetalle.forEach((v) => {
        if (!ventasHoyReales.some((vr) => (vr.id && vr.id === v.id) || (vr.ventaId && vr.ventaId === v.id))) {
          ventasHoyReales.push(v);
        }
      });
    }

    let contadoHoyCalculado = 0;
    let creditoHoyCalculado = 0;

    ventasHoyReales.forEach((v) => {
      const monto = Number(v.totalVenta || v.total || v.totalMonto || 0);
      if (esVentaCredito(v)) {
        creditoHoyCalculado += monto;
      } else {
        contadoHoyCalculado += monto;
      }
    });

    const totalHoyCalculado = contadoHoyCalculado + creditoHoyCalculado;
    const totalHoyCarlos = Number(rawCarlos?.kpis?.ventasHoy?.totalMonto || 0);
    const totalHoyFinal = Math.max(totalHoyCarlos, totalHoyCalculado);

    const creditoCarlos = Number(rawCarlos?.kpis?.ventasHoy?.credito || 0);
    const creditoHoyFinal = Math.max(creditoCarlos, creditoHoyCalculado);

    // Si Carlos reportó un total mayor pero sin desglose de crédito, deducir el contado restante
    const contadoCarlos = Number(rawCarlos?.kpis?.ventasHoy?.contado || 0);
    const contadoHoyFinal = Math.max(
      contadoCarlos,
      contadoHoyCalculado,
      totalHoyFinal > creditoHoyFinal ? totalHoyFinal - creditoHoyFinal : 0
    );

    const conteoHoyFinal = Math.max(
      Number(rawCarlos?.kpis?.ventasHoy?.cantidad || 0),
      ventasHoyReales.length
    );

    const ventasHoy = {
      conteo: conteoHoyFinal,
      totalMonto: totalHoyFinal,
      contado: contadoHoyFinal,
      credito: creditoHoyFinal,
    };

    // ----------------------------------------------------
    // B. VENTAS DEL MES Y META ACUMULADA
    // ----------------------------------------------------
    const ventasMesReales = parseArray(resVentasMes).filter((v) =>
      perteneceAEmpleado(v, empleadoId)
    );
    const totalMesCalculado = ventasMesReales.reduce(
      (sum, v) => sum + Number(v.totalVenta || v.total || 0),
      0
    );
    const totalMesCarlos = Number(rawCarlos?.kpis?.ventasMes?.totalMonto || 0);
    const totalMesFinal = Math.max(totalMesCarlos, totalMesCalculado);

    const conteoMesFinal = Math.max(
      Number(rawCarlos?.kpis?.ventasMes?.cantidad || 0),
      ventasMesReales.length
    );

    const metaMes = 250000;
    const porcentajeMeta = metaMes > 0 ? Number(((totalMesFinal / metaMes) * 100).toFixed(1)) : 0;

    const ventasMes = {
      conteo: conteoMesFinal,
      totalMonto: totalMesFinal,
      metaMes,
      porcentajeMeta,
    };

    // ----------------------------------------------------
    // C. CARTERA Y CRÉDITOS EN MORA
    // ----------------------------------------------------
    let saldoTotalPendiente = Number(
      rawCarlos?.kpis?.carteraCreditos?.saldoTotalPendiente || 0
    );
    let totalVencidos = Number(
      rawCarlos?.kpis?.carteraCreditos?.creditosVencidos || 0
    );
    let clientesConSaldo = Number(
      rawCarlos?.kpis?.carteraCreditos?.clientesConSaldo || 0
    );

    const creditosCarlos = Array.isArray(rawCarlos?.creditosPendientesDetalle)
      ? [...rawCarlos.creditosPendientesDetalle]
      : [];

    // Enriquecer con ventas a crédito del mes con saldo pendiente
    const ventasCreditoMes = ventasMesReales.filter(esVentaCredito);
    ventasCreditoMes.forEach((v) => {
      const creditoId = v.creditoId || v.creditos?.[0]?.id || v.id;
      const yaRegistrado = creditosCarlos.some(
        (c) => c.creditoId === creditoId || c.id === creditoId
      );
      const saldo = Number(v.saldoCredito ?? v.creditos?.[0]?.saldo ?? v.saldo ?? v.totalVenta ?? 0);
      if (!yaRegistrado && saldo > 0) {
        saldoTotalPendiente += saldo;
        clientesConSaldo++;
      }
    });

    // Calcular créditos en mora
    const creditosMora = creditosCarlos.filter(
      (c) => Number(c.diasMora || 0) > 0 || (c.estadoMora || '').toLowerCase() === 'vencido'
    );
    const saldoMoraCalculado = creditosMora.reduce(
      (sum, c) => sum + Number(c.saldo || 0),
      0
    );

    const clientesMora = (creditosMora.length > 0 ? creditosMora : creditosCarlos)
      .slice(0, 10)
      .map((c) => ({
        id: c.creditoId || c.id,
        nombre: c.cliente || c.Cliente?.nombreCorto || 'Cliente General',
        diasAtraso: Number(c.diasMora || 0),
        saldoVencido: Number(c.saldo || 0),
        fechaVencimiento: c.fechaVencimiento ? moment(c.fechaVencimiento).format('YYYY-MM-DD') : '',
        telefono: c.telefono || '',
      }));

    const carteraCreditos = {
      saldoMora: saldoMoraCalculado > 0 ? saldoMoraCalculado : saldoTotalPendiente,
      clientesEnMoraConteo: totalVencidos > 0 ? totalVencidos : (clientesMora.length || clientesConSaldo),
      saldoTotalPendiente,
      clientesMora,
    };

    // ----------------------------------------------------
    // D. PEDIDOS ASIGNADOS (Fusión en vivo BD + Carlos)
    // ----------------------------------------------------
    const pedidosReales = parseArray(resPedidos).filter((p) =>
      perteneceAEmpleado(p, empleadoId)
    );
    const pedidosCarlos = Array.isArray(rawCarlos?.pedidosPendientesDetalle)
      ? rawCarlos.pedidosPendientesDetalle
      : [];

    const mapaPedidos = new Map();

    // 1. Pedidos vivos de la BD (más recientes primero)
    pedidosReales.forEach((p) => {
      const id = p.id;
      const clienteNombre =
        p.Cliente?.nombreCorto ||
        p.Cliente?.nombreCompleto ||
        (p.Cliente ? `${p.Cliente.primerNombre || ''} ${p.Cliente.primerApellido || ''}`.trim() : '') ||
        p.cliente?.nombreCompleto ||
        p.clienteNombre ||
        `Cliente #${p.clienteId || p.id}`;

      const totalCalculado =
        p.total !== undefined && p.total !== null && Number(p.total) > 0
          ? Number(p.total)
          : (p.pedidoDetalles?.reduce(
              (s, it) => s + Number(it.precio || 0) * Number(it.cantidad || 1),
              0
            ) || 0);

      const direccionEntrega =
        p.PedidoDireccions?.[0]?.direccion ||
        p.direccion ||
        p.direccionEntrega ||
        p.notas ||
        'Dirección registrada';

      const telefonoContacto =
        p.Cliente?.ClienteTelefonos?.[0]?.telefono ||
        p.Cliente?.telefono ||
        p.telefono ||
        'Sin teléfono';

      mapaPedidos.set(id, {
        id,
        cliente: clienteNombre,
        fecha: p.fecha ? moment(p.fecha).format('YYYY-MM-DD') : fechaQuery,
        hora: p.hora || (p.fecha ? moment(p.fecha).format('hh:mm A') : '09:00 AM'),
        total: totalCalculado,
        estado: p.estado || 'Programada',
        direccion: direccionEntrega,
        contacto: telefonoContacto,
      });
    });

    // 2. Complementar con pedidos históricos reportados por el backend
    pedidosCarlos.forEach((p) => {
      if (!mapaPedidos.has(p.id)) {
        mapaPedidos.set(p.id, {
          id: p.id,
          cliente: p.cliente || 'Cliente General',
          fecha: p.fecha ? moment(p.fecha).format('YYYY-MM-DD') : fechaQuery,
          hora: p.fecha ? moment(p.fecha).format('hh:mm A') : '09:00 AM',
          total: Number(p.total || 0),
          estado: p.estado || 'Programada',
          direccion: p.direccion || (p.empresa && p.empresa !== 'N/A' ? p.empresa : 'Dirección registrada'),
          contacto: p.telefono || 'Sin teléfono',
        });
      }
    });

    const listaPedidos = Array.from(mapaPedidos.values());
    const pendientes = listaPedidos.filter((p) => (p.estado || '').toLowerCase() === 'pendiente');
    const programadas = listaPedidos.filter((p) => (p.estado || '').toLowerCase() === 'programada');
    const enRuta = listaPedidos.filter((p) => (p.estado || '').toLowerCase() === 'en ruta');
    const hoy = listaPedidos.filter((p) => p.fecha && moment(p.fecha).isSame(moment(), 'day'));

    const totalAsignadosFinal = Math.max(
      Number(rawCarlos?.kpis?.pedidosPendientes || 0),
      listaPedidos.length
    );

    const pedidosAsignados = {
      totalAsignados: totalAsignadosFinal,
      totalPendientes: pendientes.length,
      totalProgramadas: programadas.length,
      totalEnRuta: enRuta.length,
      totalHoy: hoy.length,
      items: listaPedidos.slice(0, 10),
    };

    // ----------------------------------------------------
    // E. COTIZACIONES DEL MES
    // ----------------------------------------------------
    const cotizacionesReales = parseArray(resCotizaciones).filter((c) =>
      perteneceAEmpleado(c, empleadoId)
    );

    const totalCotizacionesMonto = cotizacionesReales.reduce((sum, c) => {
      const monto =
        c.total !== undefined && c.total !== null && Number(c.total) > 0
          ? Number(c.total)
          : (c.cotizacionDetalles?.reduce(
              (s, it) => s + Number(it.precio || 0) * Number(it.cantidad || 1),
              0
            ) || 0);
      return sum + monto;
    }, 0);

    const pendientesAprobacion = cotizacionesReales.filter(
      (c) =>
        (c.estado || '').toLowerCase() === 'pendiente' ||
        (c.estado || '').toLowerCase() === 'emitida'
    ).length;

    const cotizacionesAprobadas = cotizacionesReales.filter(
      (c) => (c.estado || '').toLowerCase() === 'aprobada'
    ).length;

    const conteoCotizacionesFinal = Math.max(
      Number(rawCarlos?.kpis?.cotizacionesMes || 0),
      cotizacionesReales.length
    );
    const montoCotizacionesFinal = Math.max(
      Number(rawCarlos?.kpis?.cotizacionesMonto || 0),
      totalCotizacionesMonto
    );

    const cotizaciones = {
      mesActualConteo: conteoCotizacionesFinal,
      mesActualMonto: montoCotizacionesFinal,
      pendientesAprobacion: Math.max(
        Number(rawCarlos?.kpis?.cotizacionesPendientes || 0),
        pendientesAprobacion
      ),
      tasaCierre:
        cotizacionesReales.length > 0
          ? Number(((cotizacionesAprobadas / cotizacionesReales.length) * 100).toFixed(1))
          : 0,
      ultimasCotizaciones: cotizacionesReales.slice(0, 6).map((c) => ({
        id: c.id,
        cliente:
          c.Cliente?.nombreCorto ||
          c.Cliente?.nombreCompleto ||
          (c.Cliente ? `${c.Cliente.primerNombre || ''} ${c.Cliente.primerApellido || ''}`.trim() : '') ||
          c.cliente?.nombreCompleto ||
          c.clienteNombre ||
          `Cliente #${c.id}`,
        monto: Number(c.total || 0),
        fecha: c.fecha ? moment(c.fecha).format('YYYY-MM-DD') : fechaQuery,
        estado: c.estado || 'Pendiente',
      })),
    };

    // ----------------------------------------------------
    // F. ENSAMBLAJE DE DATOS CONSOLIDADOS
    // ----------------------------------------------------
    const datosFinales = {
      empleado: rawCarlos?.empleado || {
        id: esConsolidadoGlobal ? 'TODOS' : empleadoId,
        nombreCompleto: esConsolidadoGlobal ? 'Toda la Empresa' : 'Colaborador',
        cargo: esConsolidadoGlobal ? 'Consolidado General' : 'Asesor Comercial / Ventas',
        fechaConsulta: fechaQuery,
      },
      ventas: {
        hoy: ventasHoy,
        mesActual: ventasMes,
      },
      pedidosAsignados,
      cotizaciones,
      carteraCreditos,
      cobranzaCredito: {
        saldoMora: carteraCreditos.saldoMora,
        clientesEnMora: carteraCreditos.clientesMora,
        saldoTotalPendiente: carteraCreditos.saldoTotalPendiente,
      },
    };

    return {
      datos: datosFinales,
      origen: 'API_REAL',
    };
  },
};
